// src/pages/marketplace/developer/DeveloperPublishPage.tsx
// Pre-publish checklist and publish action for the developer portal.

import { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import type { ProductStatus as ApiProductStatus } from '@core/types/product';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Alert,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '../../../components/StatusBadge';
import { Select } from '@/components/Select';

const TABS = [
  { key: 'edit', label: 'General', path: (id: string) => `/developer/products/${id}/edit` },
  { key: 'marketplace', label: 'Marketplace', path: (id: string) => `/developer/products/${id}/marketplace` },
  { key: 'dependencies', label: 'Dependencies', path: (id: string) => `/developer/products/${id}/dependencies` },
  { key: 'reviews', label: 'Reviews', path: (id: string) => `/developer/products/${id}/reviews` },
  { key: 'publish', label: 'Publish', path: (id: string) => `/developer/products/${id}/publish` },
] as const;

const STATUSES = ['DRAFT', 'BETA', 'ACTIVE', 'MAINTENANCE', 'DEPRECATED'] as const;

function statusVariant(status: string): string {
  switch (status) {
    case 'ACTIVE': return 'success';
    case 'BETA': return 'info';
    case 'DRAFT': return 'secondary';
    case 'MAINTENANCE': return 'warning';
    case 'DEPRECATED': return 'error';
    default: return 'secondary';
  }
}

interface CheckItem {
  key: string;
  label: string;
  passed: boolean;
}

export const DeveloperPublishPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useProduct();
  const { t } = useI18n();
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusValue, setStatusValue] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [metadata, setMetadata] = useState<any>(null);

  useEffect(() => {
    if (id) {
      store.GetProduct(id);
      loadMetadata();
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  const loadMetadata = async () => {
    if (!id) return;
    try {
      const result = await store.GetMarketplaceMetadata(id);
      setMetadata(result);
    } catch {
      // Metadata may not exist yet
    }
  };

  const item = store.currentItem as any;
  const currentStatus = item?.status || 'DRAFT';

  useEffect(() => {
    if (item?.status) {
      setStatusValue(item.status);
    }
  }, [item?.status]);

  const checklist = useMemo((): CheckItem[] => {
    if (!item) return [];

    const screenshots = metadata?.screenshot_urls ?? [];
    const redirectUris = Array.isArray(item.redirect_uris)
      ? item.redirect_uris
      : (typeof item.redirect_uris === 'string' && item.redirect_uris
          ? item.redirect_uris.split(',').filter(Boolean)
          : []);

    return [
      {
        key: 'name',
        label: 'Product name filled',
        passed: !!(item.name && item.name.trim()),
      },
      {
        key: 'description',
        label: 'Description filled',
        passed: !!(item.description && item.description.trim()),
      },
      {
        key: 'screenshot',
        label: 'At least one screenshot',
        passed: screenshots.length > 0,
      },
      {
        key: 'docs_url',
        label: 'Documentation URL provided',
        passed: !!(item.docs_url && item.docs_url.trim()),
      },
      {
        key: 'support_url',
        label: 'Support URL provided',
        passed: !!(item.support_url && item.support_url.trim()),
      },
      {
        key: 'oauth2',
        label: 'OAuth2 configured (has redirect URIs)',
        passed: redirectUris.length > 0,
      },
      {
        key: 'legal',
        label: 'Legal entity filled',
        passed: !!(item.legal_entity && item.legal_entity.trim()),
      },
    ];
  }, [item, metadata]);

  const allChecksPassed = checklist.every((c) => c.passed);
  const passedCount = checklist.filter((c) => c.passed).length;
  const isPublished = currentStatus === 'ACTIVE';

  const handlePublish = async () => {
    if (!id) return;
    setIsPublishing(true);
    try {
      await store.PublishProduct(id, {});
      await store.GetProduct(id);
    } catch {
      // Error handled by store
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!id) return;
    if (!confirm('Are you sure you want to unpublish this product? It will no longer be visible in the marketplace.')) return;
    setIsPublishing(true);
    try {
      await store.SetProductStatus(id, { status: 'PRODUCT_STATUS_DRAFT' });
      await store.GetProduct(id);
    } catch {
      // Error handled by store
    } finally {
      setIsPublishing(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!id || !statusValue) return;
    setIsUpdatingStatus(true);
    try {
      await store.SetProductStatus(id, {
        status: (statusValue.startsWith('PRODUCT_STATUS_')
          ? statusValue
          : `PRODUCT_STATUS_${statusValue}`) as ApiProductStatus,
        reason: statusReason,
      });
      setStatusReason('');
      await store.GetProduct(id);
    } catch {
      // Error handled by store
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const productName = item?.name || 'Product';

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb. The tab already names this page; the heading stays for screen readers. */}
      <div>
        <nav className="text-sm">
          <Link to="/developer" className="text-muted-foreground hover:text-foreground">
            Developer
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/developer/products/${id}/edit`} className="text-muted-foreground hover:text-foreground">
            {productName}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Publish</span>
        </nav>
        <h1 className="sr-only">Publish</h1>
      </div>

      {/* Tab navigation */}
      <nav className="flex border-b border-border">
        {TABS.map((tab) => (
          <Link
            key={tab.key}
            to={tab.path(id || '')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab.key === 'publish'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {tab.label}
          </Link>
        ))}
      </nav>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Current status */}
      <Card>
        <CardHeader>
          <CardTitle>Current Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <StatusBadge
              variant={statusVariant(currentStatus)}
              label={currentStatus}
            />
            {item?.published_at && (
              <span className="text-sm text-muted-foreground">
                Published on {new Date(item.published_at).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Publish checklist */}
      <Card>
        <CardHeader>
          <CardTitle>Publish Checklist</CardTitle>
          <CardDescription>
            {passedCount} of {checklist.length} checks passed. All checks must pass to publish.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {checklist.map((check) => (
              <div key={check.key} className="flex items-center gap-3">
                {check.passed ? (
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
                <span className={`text-sm ${check.passed ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {check.label}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-border">
            {isPublished ? (
              <div className="space-y-3">
                <Alert variant="info">
                  This product is currently published. You can update the listing by editing the marketplace metadata, or unpublish to remove it from the marketplace.
                </Alert>
                <Button
                  variant="danger"
                  onClick={handleUnpublish}
                  disabled={isPublishing}
                >
                  {isPublishing ? 'Unpublishing...' : 'Unpublish'}
                </Button>
              </div>
            ) : allChecksPassed ? (
              <Button onClick={handlePublish} disabled={isPublishing}>
                {isPublishing ? 'Publishing...' : 'Publish to Marketplace'}
              </Button>
            ) : (
              <Alert variant="warning">
                Resolve all issues above before publishing. Complete the missing items and return here.
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Status change */}
      <Card>
        <CardHeader>
          <CardTitle>Change Product Status</CardTitle>
          <CardDescription>
            Set the product lifecycle status. This affects visibility and behavior in the marketplace.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Status</label>
            <Select value={statusValue} onChange={(e) => setStatusValue(e.target.value)}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </div>
          <Input
            label="Reason (optional)"
            type="text"
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            placeholder="Reason for status change"
          />
          <Button
            variant="secondary"
            onClick={handleStatusUpdate}
            disabled={isUpdatingStatus || statusValue === currentStatus}
          >
            {isUpdatingStatus ? 'Updating...' : 'Update Status'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
});

export default DeveloperPublishPage;
